import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Check, X, CalendarClock, Pencil, Ban, CheckCheck, Repeat } from "lucide-react";
import { STATUS, apiCall, eventSlot, euro, fmtDateLong, fmtDateTime, playerLabel } from "./calendarUtils";
import { EditForm } from "./EditForm";
import { MoveForm } from "./MoveForm";
import { ProposalCard } from "../proposals/Proposals";

const PAY = { bank_transfer: "Überweisung", paypal: "PayPal", cash: "Barzahlung", credit: "Paket-Guthaben", training_card: "Trainingskarte" };

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-white/5 text-sm">
    <span className="text-[#71717A]">{label}</span>
    <span className="text-white text-right break-words" data-testid={testId}>{children || "–"}</span>
  </div>
);

const waLink = (phone) => {
  const d = (phone || "").replace(/\D/g, "").replace(/^00/, "").replace(/^0/, "49");
  return d ? `https://wa.me/${d}` : null;
};

function Details({ e }) {
  const slot = eventSlot(e);
  const s = STATUS[e.display_status] || STATUS.pending;
  return (
    <div>
      <Row label="Status" testId="detail-status"><span className={s.text}>{s.label}</span></Row>
      <Row label="Kunde" testId="detail-customer">{e.customer?.name}</Row>
      {playerLabel(e) && <Row label="Spieler" testId="detail-player">{playerLabel(e)}</Row>}
      <Row label="Telefon" testId="detail-phone">{e.customer?.phone}</Row>
      <Row label="E-Mail" testId="detail-email">{e.customer?.email}</Row>
      <Row label="Leistung" testId="detail-training">{e.training_name}</Row>
      <Row label="Datum" testId="detail-date">{fmtDateLong(slot.date)}</Row>
      <Row label="Uhrzeit" testId="detail-time">{slot.time}–{slot.end_time} Uhr</Row>
      {e.status === "alternative_proposed" && <Row label="Ursprünglicher Wunsch">{fmtDateLong(e.date)}, {e.time} Uhr</Row>}
      <Row label="Dauer">{e.duration} Minuten</Row>
      <Row label="Trainingsort">{e.training_location}</Row>
      {e.category === "gruppe" && <Row label="Teilnehmer" testId="detail-players">{e.player_count}</Row>}
      <Row label="Preis" testId="detail-price">{e.payment_method === "training_card" ? "Trainingskarte" : euro(e.total_price)}</Row>
      <Row label="Zahlung">{PAY[e.payment_method] || e.payment_method}</Row>
      <Row label="Anfrage vom" testId="detail-created">{fmtDateTime(e.created_at)}</Row>
      <Row label="Termin-ID">{e.appointment_id}</Row>
      {e.cancelled_at && <Row label="Abgesagt am" testId="detail-cancelled-at">{fmtDateTime(e.cancelled_at)} · {e.cancelled_by === "customer" ? `vom Kunden online${e.cancellation?.hours_before != null ? ` (${String(e.cancellation.hours_before).replace(".", ",")} Std. vorher)` : ""}` : "vom Trainer"}</Row>}
      {e.cancellation?.settlement_text && <Row label="Abrechnung" testId="detail-cancel-settlement">{e.cancellation.settlement_text}</Row>}
      {(e.player_cancellations || []).map((p) => <Row key={p.at} label="Spieler abgemeldet" testId="detail-player-cancel">{p.players} ({p.from} → {p.to}) am {fmtDateTime(p.at)}</Row>)}
      {e.message && <div className="mt-3 bg-[#0A0A0A] border border-white/10 p-3 text-sm text-[#D4D4D8] whitespace-pre-wrap" data-testid="detail-message"><span className="block text-xs text-[#71717A] mb-1">Nachricht des Kunden</span>{e.message}</div>}
      {e.admin_notes && <div className="mt-2 bg-[#c1ff72]/5 border border-[#c1ff72]/20 p-3 text-sm text-[#D4D4D8] whitespace-pre-wrap"><span className="block text-xs text-[#c1ff72] mb-1">Interne Notiz</span>{e.admin_notes}</div>}
    </div>
  );
}

const ActionBtn = ({ icon: Icon, children, className = "", ...p }) => (
  <Button size="sm" className={`rounded-none ${className}`} {...p}><Icon className="w-4 h-4 mr-1.5" />{children}</Button>
);

export function AppointmentDialog({ token, event: e, onClose, onChanged, initialMode = "view" }) {
  const [mode, setMode] = useState(initialMode);
  const [busy, setBusy] = useState(false);
  const [forceConfirm, setForceConfirm] = useState(null);
  const [proposal, setProposal] = useState(null);
  if (!e) return null;

  const run = async (path, method, body, msg) => {
    setBusy(true);
    try {
      await apiCall(token, path, { method, body });
      toast.success(msg);
      setForceConfirm(null);
      onChanged();
    } catch (err) {
      if (path.includes("/confirm") && err.status === 409 && !err.message.includes("belegt")) setForceConfirm(err.message);
      else toast.error(err.message);
    }
    setBusy(false);
  };
  const confirm = (force) => run(`/appointments/admin/${e.appointment_id}/confirm${force ? "?force=true" : ""}`, "POST", null, "Termin bestätigt – Kunde wird informiert");
  const setStatus = (status, msg) => {
    if (status === "cancelled") return apiCall(token, "/admin/proposals", { method: "POST", body: { type: "appointment_cancel", payload: { appointment_id: e.appointment_id } } }).then(setProposal).catch((err) => toast.error(err.message));
    if (window.confirm(`${msg}?`)) run(`/appointments/admin/${e.appointment_id}/status`, "PATCH", { status }, `${msg} – gespeichert`);
  };
  const reject = () => { if (window.confirm(e.status === "alternative_proposed" ? "Vorschlag zurückziehen und Anfrage ablehnen?" : "Anfrage ablehnen? Der Kunde erhält eine Absage per E-Mail.")) run(`/appointments/admin/${e.appointment_id}/reject`, "POST", null, "Anfrage abgelehnt"); };
  const done = () => { setMode("view"); onChanged(); };
  const wa = waLink(e.customer?.phone);
  const st = e.status;

  return (
    <Dialog open={!!e} onOpenChange={(o) => { if (!o) { setMode("view"); setForceConfirm(null); setProposal(null); onClose(); } }}>
      <DialogContent className="bg-[#121212] border-white/10 text-white w-[calc(100vw-1rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Bebas_Neue'] text-2xl tracking-wide">{mode === "edit" ? "Termin bearbeiten" : mode === "reschedule" ? "Termin verschieben" : mode === "alternative" ? "Alternativtermin vorschlagen" : "Termindetails"}</DialogTitle>
        </DialogHeader>
        {proposal && <div data-testid="appointment-proposal"><p className="text-xs text-[#A1A1AA] mb-2">Vorschau – es wird nichts geändert oder versendet, bevor du freigibst.</p>
          <ProposalCard token={token} p={proposal} onDone={() => { setProposal(null); done(); }} /></div>}
        {!proposal && mode === "view" && (
          <>
            <Details e={e} />
            <div className="flex flex-wrap gap-2 mt-4" data-testid="contact-actions">
              {e.customer?.phone && <a href={`tel:${e.customer.phone}`} className="inline-flex items-center text-sm border border-white/20 px-3 py-1.5 hover:border-[#c1ff72]" data-testid="contact-call"><Phone className="w-4 h-4 mr-1.5" />Anrufen</a>}
              {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center text-sm border border-white/20 px-3 py-1.5 hover:border-[#c1ff72]" data-testid="contact-whatsapp"><MessageCircle className="w-4 h-4 mr-1.5" />WhatsApp</a>}
              {e.customer?.email && <a href={`mailto:${e.customer.email}`} className="inline-flex items-center text-sm border border-white/20 px-3 py-1.5 hover:border-[#c1ff72]" data-testid="contact-email"><Mail className="w-4 h-4 mr-1.5" />E-Mail</a>}
            </div>
            {forceConfirm && (
              <div className="mt-3 border border-orange-500/50 bg-orange-500/10 p-3 text-sm text-orange-200" data-testid="confirm-force-box">
                {forceConfirm}
                <Button size="sm" disabled={busy} onClick={() => confirm(true)} className="ml-3 mt-2 bg-orange-500 hover:bg-orange-600 text-black rounded-none" data-testid="confirm-force">Trotzdem bestätigen</Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10" data-testid="appointment-actions">
              {st === "pending" && <ActionBtn icon={Check} disabled={busy} onClick={() => confirm(false)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="action-confirm">Bestätigen</ActionBtn>}
              {st === "pending" && <ActionBtn icon={Repeat} disabled={busy} onClick={() => setMode("alternative")} className="bg-orange-500 hover:bg-orange-600 text-black" data-testid="action-alternative">Alternative vorschlagen</ActionBtn>}
              {(st === "pending" || st === "alternative_proposed") && <ActionBtn icon={X} disabled={busy} onClick={reject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="action-reject">{st === "pending" ? "Ablehnen" : "Vorschlag zurückziehen"}</ActionBtn>}
              {(st === "pending" || st === "confirmed") && <ActionBtn icon={CalendarClock} disabled={busy} onClick={() => setMode("reschedule")} variant="outline" className="border-white/20 text-white" data-testid="action-reschedule">Verschieben</ActionBtn>}
              {st === "confirmed" && <ActionBtn icon={Ban} disabled={busy} onClick={() => setStatus("cancelled", "Termin absagen")} className="bg-red-600 hover:bg-red-700 text-white" data-testid="action-cancel">Absagen</ActionBtn>}
              {st === "confirmed" && <ActionBtn icon={CheckCheck} disabled={busy} onClick={() => setStatus("completed", "Als abgeschlossen markieren")} variant="outline" className="border-white/20 text-white" data-testid="action-complete">Abgeschlossen</ActionBtn>}
              <ActionBtn icon={Pencil} disabled={busy} onClick={() => setMode("edit")} variant="outline" className="border-white/20 text-white" data-testid="action-edit">Bearbeiten</ActionBtn>
            </div>
          </>
        )}
        {!proposal && mode === "edit" && <EditForm token={token} e={e} onDone={done} onCancel={() => setMode("view")} />}
        {!proposal && (mode === "reschedule" || mode === "alternative") && <MoveForm token={token} e={e} mode={mode} onDone={done} onCancel={() => setMode("view")} onProposal={setProposal} />}
      </DialogContent>
    </Dialog>
  );
}
